import { type BitmapText, type Text, Ticker } from 'pixi.js';
import type { MagicLayout } from '@magic/layout';
import type { PopupController } from 'controllers/Popup.controller';
import type { SymbolImagesController } from 'controllers/SymbolImages.controller';
import { HtmlScrollView } from 'ui/HtmlScrollView';
import { markdownToHtml, splitLeadingHeading } from '../utils/markdown';
import { isIdle } from '../stores/api.store';

export type RulesPopupOptions = {
    /** Markdown file to render, relative to the page. */
    file?: string;
    /** Registered id of the popup skeleton, and the slot on it the page is placed in. */
    spineID?: string;
    contentSlot?: string;
    /**
     * Size of the scrolling area, in the popup skeleton's units. The default is a box inside a
     * 1026×734 panel that clears both the title and the close button — measure it against the
     * game's own popup art.
     */
    width?: number;
    height?: number;
    /**
     * Shifts the area off the content slot's own position, in the skeleton's units but with `y`
     * counting *down* the screen — a slot object is placed in Pixi's orientation, not Spine's.
     * Use it to centre the page in the gap between the title and the close button when the slot
     * itself does not sit in the middle of it.
     */
    offset?: { x?: number; y?: number };
    /** Popup title. Defaults to the document's first `# heading`. */
    title?: string;
    /**
     * Label for the popup's accept button. The rules are shown with the close X alone,
     * which carries no text, so this only reaches the screen on a skeleton exported
     * before the popup's buttons split.
     */
    closeLabel?: string;
    /**
     * Text keys hidden for as long as the page is up, on top of the popup's own content text.
     * For a game whose popup close button is drawn over another button — this one's lands on the
     * spin button — that button's label would otherwise show through the close label.
     */
    hideTexts?: string[];
    /** Extra CSS for the page, appended to the panel's stylesheet. */
    css?: string;
};

const DEFAULTS = {
    file: 'assets/static/rules.md',
    spineID: 'popup',
    contentSlot: 'text_popup_content',
    width: 880,
    height: 520,
    closeLabel: 'close',
    hideTexts: [] as string[],
} as const;

/** How long the closing fade is given before the page is pulled regardless. */
const CLOSE_TIMEOUT_MS = 800;

/**
 * The rules page: a Markdown file rendered as scrolling HTML inside the popup, opened by the
 * UI's info button.
 *
 * The popup itself is the shared one — same skeleton, same show state, same close button — so it
 * goes through {@link PopupController} like every other popup, and this only owns what the
 * skeleton's own text slots cannot do: a page of HTML that scrolls. Sharing is also why the page
 * only opens from idle; mid-round the round controller needs that popup for its own.
 */
export class RulesPopupController {
    private readonly options: Required<Omit<RulesPopupOptions, 'title' | 'css' | 'offset'>> &
        Pick<RulesPopupOptions, 'title' | 'css' | 'offset'>;

    private view?: HtmlScrollView;
    private readonly ready: Promise<HtmlScrollView | undefined>;
    private title = '';
    /** The text nodes hidden while the page is up: the popup's own content line, plus `hideTexts`. */
    private hidden: (Text | BitmapText)[] = [];

    private open = false;
    private closing = false;
    private closingMs = 0;

    constructor(
        private spine: MagicLayout,
        private popup: PopupController,
        options: RulesPopupOptions = {},
        /** Renders the symbol art the page's `` `H1` ``-style codes stand in for. */
        private symbolImages?: SymbolImagesController,
    ) {
        this.options = { ...DEFAULTS, ...options };

        // built up front so the first press of the info button opens instantly
        this.ready = this.build();

        // `info_down` is the info button's pointerdown — the same event that plays
        // `event_info_down/info_click`, so the button's press animation and the popup
        // start together
        this.spine.animations.addEventListener('info_down', () => void this.show());
    }

    private async build(): Promise<HtmlScrollView | undefined> {
        const { file, width, height, css, spineID, contentSlot } = this.options;

        let markdown: string;
        try {
            const response = await fetch(file);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            markdown = await response.text();
        } catch (error) {
            console.error(`Failed to load the rules page "${file}":`, error);
            return;
        }

        const popupSpine = this.spine.getSpine(spineID);
        if (!popupSpine) {
            console.error(`Rules popup: spine "${spineID}" not found`);
            return;
        }

        // the object the texts controller put in the content slot: it already carries that
        // slot's transform, so the page rides along by being its child. Attaching to the slot
        // itself would evict it — a slot holds one object.
        const anchor = popupSpine.skeleton.findSlot(contentSlot)
            ? popupSpine.getSlotObject(contentSlot)
            : undefined;

        if (!anchor) {
            console.error(`Rules popup: slot "${contentSlot}" of "${spineID}" holds no object`);
            return;
        }

        const { title, body } = splitLeadingHeading(markdown);
        this.title = this.options.title ?? title ?? '';

        // the symbol art started rendering when the game did, in parallel with the fetch above;
        // the page waits for it rather than going out with the bare codes in its place
        await this.symbolImages?.ready;

        const view = new HtmlScrollView({ width, height, css });
        view.html = markdownToHtml(body, {
            symbolImage: (name) => this.symbolImages?.get(name),
        });
        view.position.set(this.options.offset?.x ?? 0, this.options.offset?.y ?? 0);
        anchor.addChild(view);

        const texts = this.spine.texts.getInstances();
        this.hidden = [contentSlot.replace(/^text_/, ''), ...this.options.hideTexts]
            .map((key) => texts.get(key))
            .filter((text): text is Text | BitmapText => !!text);

        this.view = view;

        return view;
    }

    private async show() {
        if (this.open || !isIdle()) return;
        this.open = true;

        const view = await this.ready;
        if (!view) {
            this.open = false;
            return;
        }

        // the skeleton's content text is sized for a short value (a free spins count, a win
        // figure); the rules are a page of HTML, so it steps aside until the page is closed
        this.hidden.forEach((text) => (text.visible = false));

        this.closing = false;
        this.closingMs = 0;
        view.open();
        Ticker.shared.add(this.follow);

        // the popup controller owns the skeleton — it plays the show state and resolves once
        // the player presses close. The page is only read, so it is shown with the close
        // button alone: there is nothing here to accept.
        await this.popup.show({
            layout: 'only_close',
            title: this.title,
            buttonText: this.options.closeLabel,
        });

        // the close button's own `hide_popup` is now fading the popup out; the page stays on it
        // until that has finished (see `follow`)
        this.closing = true;
    }

    /**
     * Keeps the page on the popup, at the popup's own opacity.
     *
     * The popup skeleton is a slot object of the UI skeleton, so its container alpha *is* the
     * animated `spine_popup` slot alpha — the page therefore fades in and out with the popup for
     * free, and the close completes once that fade has run out.
     */
    private follow = (ticker: Ticker) => {
        const popupSpine = this.spine.getSpine(this.options.spineID);
        const opacity = popupSpine?.visible ? popupSpine.alpha : 0;

        this.view?.sync(opacity);

        if (!this.closing) return;

        this.closingMs += ticker.deltaMS;
        if (opacity <= 0 || this.closingMs >= CLOSE_TIMEOUT_MS) this.finishClose();
    };

    private finishClose() {
        this.open = false;
        this.closing = false;
        Ticker.shared.remove(this.follow);
        this.view?.close();

        this.hidden.forEach((text) => (text.visible = true));
    }

    destroy() {
        Ticker.shared.remove(this.follow);
        this.view?.destroy();
        this.view = undefined;
    }
}
